export type EntitySelectItem = {
  id: string;
  title?: string;
  url?: string;
  parentId?: string;
};

type SelectedItemActivatedEvent = CustomEvent<{
  value?: EntitySelectItem;
}>;

/**
 * Navigate when the admin header entity picker selects an item (uses item.url from items JSON).
 */
export function navigateForSelectedEntityItem(item: EntitySelectItem | undefined | null): boolean {
  if (!item?.url) {
    return false;
  }

  const targetPath = item.url.startsWith('/')
    ? item.url.split('?')[0].split('#')[0]
    : (() => {
        try {
          return new URL(item.url, globalThis.location.origin).pathname;
        } catch {
          return '';
        }
      })();

  if (!targetPath) {
    return false;
  }

  if (targetPath === globalThis.location.pathname) {
    return false;
  }

  // Dynamic import keeps this module free of a hard cycle with admin boot.
  void import('./common/admin-nav-loading').then(({navigateAdminPage}) => {
    navigateAdminPage(targetPath);
  }).catch(() => {
    globalThis.location.assign(targetPath);
  });
  return true;
}

/**
 * Header org picker: navigate after a real user gesture. Prefer
 * `selected-item-activated` (cbar-entity-select ≥4.7 / cbar-select ≥2.6) so
 * re-picking the already-selected org/account/campaign still navigates when
 * the item URL differs from the current path (e.g. deep page → dashboard).
 */
export function shouldNavigateOrganisationSelection(
  userHasInteracted: boolean,
  selectedId: string | undefined,
): boolean {
  return Boolean(userHasInteracted && selectedId);
}

export async function setupOrganisationDropdownListeners(): Promise<void> {
  await customElements.whenDefined('cbar-entity-select');

  const organisationDropdown = document.querySelector(
    '#organisation_select'
  ) as CbarEntitySelectElement | null;

  if (!organisationDropdown || organisationDropdown.dataset.orgNavBound === 'true') {
    return;
  }

  organisationDropdown.dataset.orgNavBound = 'true';

  let userHasInteracted = false;
  const markInteracted = () => {
    userHasInteracted = true;
  };

  // pointerdown/keydown cover mouse, touch, and keyboard without treating
  // programmatic focus (if any) as a navigation intent.
  organisationDropdown.addEventListener('pointerdown', markInteracted);
  organisationDropdown.addEventListener('keydown', markInteracted);

  // selected-item-activated fires after value is committed, including when the
  // user re-selects the current item (selected-item-changed stays silent then).
  organisationDropdown.addEventListener('selected-item-activated', (event: Event) => {
    const item = (event as SelectedItemActivatedEvent).detail?.value;
    if (!shouldNavigateOrganisationSelection(userHasInteracted, item?.id)) {
      return;
    }

    navigateForSelectedEntityItem(item);
  });
}

function setupHeaderSearch() {
  document.addEventListener('search-submitted', (e: Event) => {
    const detail = (e as CustomEvent<string>).detail;
    if (detail) {
      window.location.href =
        '/admin/searchentities?search_string=' + encodeURIComponent(detail);
    }
  });
}

export function handleHeaderSidenav() {
  setupHeaderSearch();
}
